import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { gtplSaathiAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  PlusSquare, Eye, Send, StopCircle, RefreshCw, Loader, CheckCircle, XCircle, AlertTriangle, Ban, Image, X, RotateCcw, Pause, Play, Repeat, Users, ListChecks, Activity, Tv, Radio, Wallet, Search,
} from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';
import AreaMultiSelect from '../components/common/AreaMultiSelect';
import { Screen, PageHead, Panel, Field, Notice, Pill, Stats, Stat, Item, Empty, Pager, Progress, Tabs, Loading, Details } from '../components/gtpl/GtplKit';

// GTPL → A la Carte Bulk, catalog-driven (2026-11). Staff pick what to add — one or more
// packages from a single broadcaster bouquet, or one or more individual A la Carte channels —
// then Regular/DTD/AMP customers, then Preview → Place. GTPL allows only one broadcaster per
// order, so a broadcaster run is scoped to a single broadcaster; individual channels may mix.
// The catalog is scraped read-only from GTPL and cached by the backend (see
// gtplSaathiService.getAlacarteCatalog). Nothing is submitted by Preview: it stops at GTPL's
// final confirmation, and Place uses the button picked from that preview.
// Safety: channels priced above Rs. 0 are only selectable after ticking "Allow paid channels";
// broadcaster bouquets are always paid, so their total is shown in the confirmation dialog.

const DURATION_TYPES = [
  { value: 'regular', label: 'Regular' }, { value: 'dtd', label: 'DTD' }, { value: 'amp', label: 'AMP' },
];
const typesLabel = (types) => (!types?.length || types.length === DURATION_TYPES.length
  ? 'all types'
  : DURATION_TYPES.filter((t) => types.includes(t.value)).map((t) => t.label).join(' + '));

const VALIDITY_OPTIONS = [
  { value: 'D30', label: '1 Month' }, { value: 'D60', label: '2 Months' }, { value: 'D90', label: '3 Months' },
  { value: 'D180', label: '6 Months' }, { value: 'D360', label: '12 Months' },
];

const STATUS_STYLE = {
  placed: { label: 'Placed', tone: 'ok' },
  previewed: { label: 'Previewed', tone: 'info' },
  already_active: { label: 'Already active', tone: 'mute' },
  skipped: { label: 'Skipped', tone: 'mute' },
  unconfirmed: { label: 'Unconfirmed', tone: 'warn' },
  failed: { label: 'Failed', tone: 'bad' },
};

const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');
const fmtRs = (n) => (n == null ? '—' : `Rs. ${Number(n).toLocaleString('en-IN')}`);
const safeLabel = (v) => String(v || '').toUpperCase().replace(/[^A-Z0-9 &+.-]/g, '').replace(/\s+/g, ' ').trim().slice(0, 300);

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || { label: status, tone: 'mute' };
  return <Pill tone={s.tone} dot>{s.label}</Pill>;
}

function ScreenshotModal({ row, onClose }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let objectUrl;
    gtplSaathiAPI.getAlacarteScreenshot(row._id)
      .then(({ data }) => { objectUrl = URL.createObjectURL(data); setUrl(objectUrl); })
      .catch(() => toast.error('Screenshot not available'));
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [row._id]);
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 1000, width: '95vw' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>GTPL page before final submit — {row.accountNumber}</h3>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body" style={{ maxHeight: '75vh', overflow: 'auto' }}>
          {url ? <img src={url} alt="GTPL order page" style={{ width: '100%', border: '1px solid var(--border)' }} />
            : <div style={{ color: 'var(--text-muted)' }}><Loader size={13} style={{ animation: 'spin 1s linear infinite' }} /> Loading…</div>}
        </div>
      </div>
    </div>
  );
}

function RestartModal({ summary, busy, onRestart, onClose }) {
  const [keepOld, setKeepOld] = useState(false);
  const ch = summary?.channelName;
  const done = (summary?.placed ?? 0) + (summary?.alreadyActive ?? 0) + (summary?.carriedOver ?? 0);
  const option = (value, title, text) => (
    <label className={`gx-item${keepOld === value ? ' is-selected' : ''}`} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
      <input type="radio" name="keepOld" checked={keepOld === value} onChange={() => setKeepOld(value)} style={{ marginTop: 3 }} />
      <span><strong>{title}</strong><div style={{ fontSize: '.8rem', color: 'var(--text-muted)', marginTop: 2 }}>{text}</div></span>
    </label>
  );
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal gtpl-ui" style={{ maxWidth: 520, width: '95vw' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Restart {ch} — new round</h3>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body" style={{ display: 'grid', gap: 10 }}>
          {option(false, 'Ignore old data', `Every active customer becomes eligible for ${ch} again, including the ${done} already placed or who already had it.`)}
          {option(true, 'Keep old data', `The ${done} customers already placed or who already had ${ch} stay skipped. Failed, unconfirmed and never-tried customers are taken again.`)}
          <div style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>
            Earlier results stay as history either way. Nothing is ordered until you run Preview / Place.
          </div>
        </div>
        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-sm btn-danger" disabled={busy} onClick={() => onRestart(keepOld)}>
            <Repeat size={13} /> Start new round
          </button>
        </div>
      </div>
    </div>
  );
}

export default function GtplAlacarteBulkPage() {
  useDarkReady();
  const { hasModuleRight } = useAuth();
  const canOrder = hasModuleRight('gtplSaathi', 'placeOrder');

  const [kind, setKind] = useState('broadcaster');
  const [validity, setValidity] = useState('D360');
  const [durationTypes, setDurationTypes] = useState(() => DURATION_TYPES.map((t) => t.value));
  const [allowPaid, setAllowPaid] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState('');
  const [broadcasterSearch, setBroadcasterSearch] = useState('');
  const [selectedBroadcaster, setSelectedBroadcaster] = useState('');
  const [pickedPackages, setPickedPackages] = useState(() => new Set());
  const [channelSearch, setChannelSearch] = useState('');
  const [pickedChannels, setPickedChannels] = useState(() => new Set());
  const [pool, setPool] = useState(null);
  const [poolLoading, setPoolLoading] = useState(false);
  const [pickSearch, setPickSearch] = useState('');
  const [pickAreas, setPickAreas] = useState(() => new Set());
  const [unticked, setUnticked] = useState(() => new Set());
  const [includeEarlier, setIncludeEarlier] = useState(false);
  const [showRestart, setShowRestart] = useState(false);
  const [orderButtonId, setOrderButtonId] = useState('');
  const [summary, setSummary] = useState(null);
  const [run, setRun] = useState(null);
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const [shot, setShot] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const pollRef = useRef(null);

  const loadCatalog = useCallback(async (refresh) => {
    setCatalogLoading(true); setCatalogError('');
    try {
      const { data } = await gtplSaathiAPI.getAlacarteCatalog(refresh);
      setCatalog(data.data);
      if (data.data?.warning) toast(data.data.warning, { icon: '⚠️' });
      else if (data.data?.refreshing) toast('Refreshing the catalog in the background — reload in a few minutes for the latest packages.', { icon: '⏳' });
      setSelectedBroadcaster((cur) => (data.data?.broadcasters?.some((b) => b.name === cur && b.packages.length) ? cur : data.data?.broadcasters?.find((b) => b.packages.length)?.name || ''));
      setPickedPackages((cur) => new Set([...cur].filter((n) => data.data?.broadcasters?.some((b) => b.packages.some((p) => p.label === n)))));
      setPickedChannels((cur) => new Set([...cur].filter((n) => data.data?.channels?.some((c) => c.label === n))));
    } catch (err) {
      setCatalogError(err.response?.data?.message || 'Failed to load GTPL catalog');
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  const loadCustomers = useCallback(async () => {
    setPoolLoading(true);
    try {
      const { data } = await gtplSaathiAPI.getAlacarteCustomers();
      setPool(data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load customers');
    } finally {
      setPoolLoading(false);
    }
  }, []);


  const broadcasterOptions = useMemo(() => {
    const q = broadcasterSearch.trim().toLowerCase();
    return (catalog?.broadcasters || []).filter((b) => b.packages.length && (!q || b.shortName.toLowerCase().includes(q) || b.name.toLowerCase().includes(q)));
  }, [catalog, broadcasterSearch]);

  const selectedBroadcasterData = useMemo(
    () => (catalog?.broadcasters || []).find((b) => b.name === selectedBroadcaster) || null,
    [catalog, selectedBroadcaster],
  );

  const selectedChannels = useMemo(
    () => (catalog?.channels || []).filter((c) => pickedChannels.has(c.label)),
    [catalog, pickedChannels],
  );
  const selectedPackages = useMemo(
    () => (selectedBroadcasterData?.packages || []).filter((p) => pickedPackages.has(p.label)),
    [selectedBroadcasterData, pickedPackages],
  );

  const pickedCount = kind === 'broadcaster' ? selectedPackages.length : selectedChannels.length;
  const itemTotal = kind === 'broadcaster'
    ? selectedPackages.reduce((n, p) => n + (Number(p.price) || 0), 0)
    : selectedChannels.reduce((n, c) => n + (Number(c.price) || 0), 0);
  const hasPaidChannel = kind === 'channel' && selectedChannels.some((c) => Number(c.price) > 0);
  const selectionLabel = kind === 'broadcaster'
    ? safeLabel(`${selectedBroadcasterData?.shortName || ''} - ${selectedPackages.map((p) => p.label).join(' + ')}`)
    : safeLabel(selectedChannels.map((c) => c.label).join(' + '));
  const selectionDisplay = kind === 'broadcaster'
    ? `${selectedBroadcasterData?.shortName || 'Pick a broadcaster'} — ${selectedPackages.map((p) => p.label).join(' + ') || 'no packages'}`
    : (selectedChannels.map((c) => c.label).join(' + ') || 'no channels');

  const eligible = useMemo(() => {
    // Once a selection has a summary, the picker shows only customers still remaining for it
    // (already placed / already active are skipped by the backend too). Before that, show the
    // whole active CableTV pool so staff can still see who exists.
    const list = summary ? (summary.eligible || []) : (pool?.eligible || []);
    return list.filter((c) => durationTypes.includes(c.type));
  }, [pool, summary, durationTypes]);
  const areas = useMemo(() => [...new Set(eligible.map((c) => c.area).filter(Boolean))].sort(), [eligible]);
  const areaCounts = useMemo(() => eligible.reduce((m, c) => { if (c.area) m[c.area] = (m[c.area] || 0) + 1; return m; }, {}), [eligible]);
  const shown = useMemo(() => {
    const q = pickSearch.trim().toLowerCase();
    return eligible.filter((c) => (!pickAreas.size || pickAreas.has(c.area))
      && (!q || c.name.toLowerCase().includes(q) || c.accountNumber.includes(q) || (c.address || '').toLowerCase().includes(q) || (c.area || '').toLowerCase().includes(q)));
  }, [eligible, pickSearch, pickAreas]);
  const pickFiltered = !!(pickAreas.size || pickSearch.trim());
  const ticked = eligible.filter((c) => !unticked.has(c.accountNumber)).map((c) => c.accountNumber);
  const allTicked = ticked.length === eligible.length;
  const pickAccounts = (n) => (allTicked ? undefined : (n ? ticked.slice(0, n) : ticked));
  const onlyShown = () => {
    const keep = new Set(shown.map((c) => c.accountNumber));
    setUnticked(new Set(eligible.map((c) => c.accountNumber).filter((a) => !keep.has(a))));
  };
  const togglePick = (acc) => setUnticked((cur) => { const n = new Set(cur); if (n.has(acc)) n.delete(acc); else n.add(acc); return n; });
  const allTypes = DURATION_TYPES.map((t) => t.value);
  const customerTypes = durationTypes.length === allTypes.length ? [] : durationTypes;

  const loadSummary = useCallback(async () => {
    if (!selectionLabel) { setSummary(null); return; }
    try {
      const { data } = await gtplSaathiAPI.getAlacarteSummary(selectionLabel, durationTypes.join(','));
      setSummary(data.data);
    } catch { setSummary(null); }
  }, [selectionLabel, durationTypes]);

  const rowsChannel = summary?.channelName || selectionLabel;

  const loadLatest = useCallback(async () => {
    try {
      const { data } = await gtplSaathiAPI.getAlacarteLatest();
      setRun(data.data || null);
      return data.data || null;
    } catch { return null; }
  }, []);

  useEffect(() => { loadCatalog(); loadCustomers(); loadLatest(); }, []); // eslint-disable-line

  const loadRows = useCallback(async () => {
    if (!rowsChannel) return;
    try {
      const { data } = await gtplSaathiAPI.getAlacarteRows({
        status: statusFilter || undefined, channel: rowsChannel, includeEarlier: includeEarlier || undefined, page, limit: 50,
      });
      setRows(data.rows || []);
      const preview = (data.rows || []).find((r) => r.status === 'previewed' && r.candidates?.length);
      if (preview?.candidates.length === 1) setOrderButtonId((cur) => cur || preview.candidates[0].id);
      setPagination(data.pagination || { total: 0, page: 1, pages: 1 });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load results');
    }
  }, [statusFilter, page, rowsChannel, includeEarlier]);

  useEffect(() => { setOrderButtonId(''); setSummary(null); setRows([]); setPage(1); }, [selectionLabel]); // eslint-disable-line
  useEffect(() => { if (selectionLabel) loadSummary(); }, [loadSummary]); // eslint-disable-line
  useEffect(() => { loadRows(); }, [loadRows]);

  const isActive = ['running', 'waiting_session', 'paused'].includes(run?.status);

  useEffect(() => {
    if (!isActive) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      const latest = await loadLatest();
      loadRows();
      if (!['running', 'waiting_session', 'paused'].includes(latest?.status)) loadSummary();
    }, 4000);
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [isActive, loadLatest, loadRows, loadSummary]);

  const isRunning = isActive;

  const start = async (mode, limit, retry, accounts) => {
    if (!selectionLabel) { toast.error('Pick a broadcaster package or an A la Carte channel first'); return; }
    if (!pickedCount) { toast.error('Pick at least one package or channel'); return; }
    if (!durationTypes.length) { toast.error('Tick at least one of Regular, DTD or AMP'); return; }
    if (kind === 'channel' && !allowPaid && hasPaidChannel) { toast.error('Tick "Allow paid channels" before selecting channels with a price'); return; }
    if (mode === 'place' && !orderButtonId) { toast.error('Pick the order button from a preview result first'); return; }
    if (mode === 'place') {
      const n = retry ? (retry.accountNumbers.length || summary?.failed || 'all failed') : (accounts ? accounts.length : (limit || (summary?.remaining ?? 'all remaining')));
      const who = retry
        ? (retry.accountNumbers.length === 1 ? `account ${retry.accountNumbers[0]} (retry)` : `${n} failed customer${n === 1 ? '' : 's'} (retry)`)
        : `${n} active customer${n === 1 ? '' : 's'} (${typesLabel(durationTypes)})`;
      const cost = itemTotal ? ` Estimated GTPL cost ${fmtRs(itemTotal)} per customer.` : '';
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Place ${selectionDisplay} (${VALIDITY_OPTIONS.find((v) => v.value === validity)?.label}) on GTPL for ${who}?${cost} This places real orders.`)) return;
    }
    if (retry && pickedCount === 0) {
      // A retry of a previous selection still needs a label.
      toast.error('The previous selection is not loaded — pick the same package/channel to retry.');
      return;
    }
    setBusy(true);
    try {
      const { data } = await gtplSaathiAPI.startAlacarteRun({
        mode, kind,
        channelNames: kind === 'channel' ? [...pickedChannels] : undefined,
        broadcasterName: kind === 'broadcaster' ? selectedBroadcasterData?.name : undefined,
        packageNames: kind === 'broadcaster' ? [...pickedPackages] : undefined,
        label: selectionLabel,
        allowPaid: kind === 'broadcaster' ? true : allowPaid,
        validity, durationTypes: retry?.accountNumbers?.length ? undefined : customerTypes,
        accountNumbers: retry?.accountNumbers || accounts || [], limit,
        orderButtonId: mode === 'place' ? orderButtonId : undefined,
        retryFailed: !!retry,
      });
      setRun(data.data);
      setPage(1);
      setStatusFilter('');
      setSelected(new Set());
      toast.success(mode === 'preview' ? 'Preview started' : retry ? 'Retry started — runs in the background' : 'Order run started — runs in the background');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start');
    } finally {
      setBusy(false);
    }
  };

  const restart = async (keepOld) => {
    const ch = (summary?.channelName || selectionLabel).toUpperCase();
    if (!ch) { toast.error('Pick a package or channel first'); return; }
    setBusy(true);
    try {
      await gtplSaathiAPI.restartAlacarteRound(ch, keepOld);
      setShowRestart(false);
      setOrderButtonId('');
      setStatusFilter('');
      setIncludeEarlier(false);
      setSelected(new Set());
      setPage(1);
      await loadSummary();
      toast.success(`New round started for ${ch} (${keepOld ? 'old data kept' : 'old data ignored'})`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to restart');
    } finally {
      setBusy(false);
    }
  };

  const toggleType = (value) => setDurationTypes((cur) => (cur.includes(value) ? cur.filter((t) => t !== value) : [...cur, value]));
  const togglePackage = (label) => setPickedPackages((cur) => { const n = new Set(cur); if (n.has(label)) n.delete(label); else n.add(label); return n; });
  const toggleChannel = (label) => setPickedChannels((cur) => { const n = new Set(cur); if (n.has(label)) n.delete(label); else n.add(label); return n; });

  const stop = async () => {
    try {
      const { data } = await gtplSaathiAPI.stopAlacarteRun();
      setRun(data.data);
      toast.success('Stopping after the current account');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to stop'); }
  };
  const pause = async () => {
    try {
      const { data } = await gtplSaathiAPI.pauseAlacarteRun();
      setRun(data.data);
      toast.success('Pausing after the current account');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to pause'); }
  };
  const resume = async () => {
    try {
      const { data } = await gtplSaathiAPI.resumeAlacarteRun();
      setRun(data.data);
      toast.success('Resuming');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to resume'); }
  };

  const failedOnPage = [...new Set(rows.filter((r) => r.status === 'failed').map((r) => r.accountNumber))];
  const allFailedOnPageSelected = failedOnPage.length > 0 && failedOnPage.every((a) => selected.has(a));
  const toggleSelected = (accountNumber) => setSelected((cur) => { const n = new Set(cur); if (n.has(accountNumber)) n.delete(accountNumber); else n.add(accountNumber); return n; });
  const toggleAllFailedOnPage = () => setSelected((cur) => {
    const n = new Set(cur);
    failedOnPage.forEach((a) => (allFailedOnPageSelected ? n.delete(a) : n.add(a)));
    return n;
  });

  const idle = !canOrder || busy || isRunning;
  const runStatus = !run ? null
    : run.status === 'paused' ? { tone: 'warn', icon: Pause, text: run.cancelRequested ? 'Stopping…' : !run.pauseRequested ? 'Resuming…' : 'Paused' }
      : run.status === 'waiting_session' ? { tone: 'warn', icon: Loader, text: run.cancelRequested ? 'Stopping…' : 'Paused — waiting for GTPL session' }
        : run.status === 'running' ? { tone: 'info', icon: Loader, text: run.cancelRequested ? 'Stopping…' : run.pauseRequested ? 'Pausing after this account…' : 'Running' }
          : run.status === 'failed' ? { tone: 'bad', icon: XCircle, text: 'Failed' }
            : run.status === 'cancelled' ? { tone: 'mute', icon: Ban, text: 'Stopped' }
              : { tone: 'ok', icon: CheckCircle, text: 'Completed' };

  const channelGroups = useMemo(() => {
    const q = channelSearch.trim().toLowerCase();
    return (catalog?.channelGroups || []).map((g) => ({
      ...g,
      channels: g.channels.filter((c) => !q || c.label.toLowerCase().includes(q)),
    })).filter((g) => g.channels.length);
  }, [catalog, channelSearch]);

  return (
    <Screen>
      <PageHead icon={PlusSquare} tone="warn" title="A la Carte Bulk"
        sub="Add broadcaster bouquets or individual A la Carte channels to active CableTV customers on GTPL Saathi, one account at a time in the background. Preview first; nothing is submitted until you place the order.">
        {isRunning && (run?.status === 'paused' ? (
          <button type="button" className="btn btn-sm btn-primary" disabled={!canOrder || run?.cancelRequested || !run?.pauseRequested} onClick={resume}>
            <Play size={14} /> Resume
          </button>
        ) : (
          <button type="button" className="btn btn-sm btn-warning" disabled={!canOrder || run?.cancelRequested || run?.pauseRequested} onClick={pause}>
            <Pause size={14} /> {run?.pauseRequested ? 'Pausing…' : 'Pause'}
          </button>
        ))}
        {isRunning && (
          <button type="button" className="btn btn-sm btn-danger" disabled={run?.cancelRequested} onClick={stop}>
            <StopCircle size={14} /> {run?.cancelRequested ? 'Stopping…' : 'Stop'}
          </button>
        )}
      </PageHead>

      {!canOrder && <Notice tone="bad">You need the GTPL "Place New Order" permission to use this page.</Notice>}

      {run && runStatus && (
        <Panel icon={Activity} tone={runStatus.tone} title={`Last run: ${runStatus.text}`}
          sub={`${run.mode === 'place' ? 'Place' : 'Preview'} · ${run.kind === 'broadcaster' ? 'Broadcaster' : 'Channels'} · ${run.channelName} · ${VALIDITY_OPTIONS.find((v) => v.value === run.validity)?.label || run.validity} · ${typesLabel(run.durationTypes)} · ${isActive ? 'started' : 'finished'} ${fmtDateTime(isActive ? run.startedAt : run.completedAt)}`}>
          <Stats>
            <Stat tone="info" value={`${run.processed || 0} / ${run.total || 0}`} label="Progress" />
            <Stat tone="ok" value={run.counts?.placed || 0} label="Placed" />
            <Stat tone="info" value={run.counts?.previewed || 0} label="Previewed" />
            <Stat tone="mute" value={run.counts?.already_active || 0} label="Already active" />
            <Stat tone={run.counts?.failed ? 'bad' : 'mute'} value={run.counts?.failed || 0} label="Failed" />
          </Stats>
          {run.total > 0 && <Progress value={run.processed || 0} max={run.total} tone={runStatus.tone === 'mute' ? 'info' : runStatus.tone} />}
          {run.status === 'paused' && run.pauseRequested && !run.cancelRequested && (
            <div style={{ marginTop: 12 }}><Notice tone="warn" icon={Pause}>
              Paused{run.pausedAt ? ` since ${fmtDateTime(run.pausedAt)}` : ''}, between accounts. Nothing is being ordered.
              Resume continues with the next customer; no other bulk run can start until this one is resumed or stopped.
            </Notice></div>
          )}
          {run.status === 'waiting_session' && (
            <div style={{ marginTop: 12 }}><Notice tone="warn" icon={AlertTriangle}>
              The GTPL Saathi session expired{run.waitingSince ? ` at ${fmtDateTime(run.waitingSince)}` : ''}. The run is on hold, and nothing was ordered for the account it was on.
              Log in to gtplsaathi.com and save a fresh cookie in <strong>GTPL → Session &amp; Settings</strong>; it continues from the same account within about 20 seconds.
            </Notice></div>
          )}
          {run.failMessage && <div style={{ marginTop: 12 }}><Notice tone={run.status === 'cancelled' ? 'mute' : 'bad'}>{run.failMessage}</Notice></div>}
        </Panel>
      )}

      <Panel icon={Tv} tone="info" title="1. What to add" count={pickedCount ? `${pickedCount} selected` : null}
        sub="Broadcaster bouquets are paid packages; individual channels can be free or paid. GTPL allows only one broadcaster per order, so broadcaster packages from different broadcasters need separate runs."
        right={<button type="button" className="btn btn-sm btn-outline-secondary" disabled={catalogLoading} onClick={() => loadCatalog(true)}>
          <RefreshCw size={13} /> {catalogLoading ? 'Loading…' : 'Refresh catalog'}
        </button>}>
        <Tabs value={kind} onChange={(v) => setKind(v)} tabs={[
          { key: 'broadcaster', label: 'Broadcaster packages', count: selectedPackages.length, icon: Tv },
          { key: 'channel', label: 'A la Carte channels', count: selectedChannels.length, icon: Radio },
        ]} />

        {catalogLoading && !catalog && <Loading>Reading GTPL’s catalog — this can take up to a minute the first time…</Loading>}
        {catalogError && <Notice tone="bad" actions={<button type="button" className="btn btn-sm btn-secondary" onClick={() => loadCatalog(true)}>Try again</button>}>{catalogError}</Notice>}

        {catalog && kind === 'broadcaster' && (
          <div style={{ marginTop: 14, display: 'grid', gap: 12 }}>
            <div className="gx-actions">
              <input className="form-control" style={{ flex: '1 1 240px' }} placeholder="Search broadcaster" value={broadcasterSearch} onChange={(e) => setBroadcasterSearch(e.target.value)} />
              <Pill tone="mute">{catalog.counts?.broadcasters || 0} broadcasters · {catalog.counts?.packages || 0} packages</Pill>
            </div>
            <div className="gx-list" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {broadcasterOptions.map((b) => (
                <button key={b.name} type="button" title={b.name}
                  className={`btn btn-sm ${selectedBroadcaster === b.name ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => { setSelectedBroadcaster(b.name); setPickedPackages(new Set()); }}>
                  {b.shortName} <span style={{ opacity: .7 }}>({b.packages.length})</span>
                </button>
              ))}
              {broadcasterOptions.length === 0 && <Empty title="No broadcaster matches" />}
            </div>
            {selectedBroadcasterData && (
              <>
                <div className="text-small" style={{ color: 'var(--text-muted)' }}>{selectedBroadcasterData.name}</div>
                <div className="gx-list" style={{ maxHeight: 360, overflowY: 'auto', display: 'grid', gap: 8 }}>
                  {selectedBroadcasterData.packages.map((p) => {
                    const on = pickedPackages.has(p.label);
                    return (
                      <label key={p.label} className={`gx-item${on ? ' is-selected' : ''}`} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer', padding: '9px 12px' }}>
                        <input type="checkbox" checked={on} onChange={() => togglePackage(p.label)} style={{ marginTop: 3 }} />
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <b>{p.label}</b>
                          <div className="text-small" style={{ color: 'var(--text-muted)' }}>Broadcaster bouquet</div>
                        </span>
                        <Pill tone={p.price ? 'warn' : 'ok'}>{fmtRs(p.price)}</Pill>
                      </label>
                    );
                  })}
                  {selectedBroadcasterData.packages.length === 0 && <Empty title="No packages returned for this broadcaster" />}
                </div>
              </>
            )}
          </div>
        )}

        {catalog && kind === 'channel' && (
          <div style={{ marginTop: 14, display: 'grid', gap: 12 }}>
            <div className="gx-actions">
              <input className="form-control" style={{ flex: '1 1 240px' }} placeholder="Search channel" value={channelSearch} onChange={(e) => setChannelSearch(e.target.value)} />
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '.85rem' }}>
                <input type="checkbox" checked={allowPaid} onChange={(e) => { setAllowPaid(e.target.checked); if (!e.target.checked) setPickedChannels((cur) => new Set([...cur].filter((l) => !(catalog.channels.find((c) => c.label === l)?.price > 0)))); }} />
                Allow paid channels
              </label>
              <Pill tone="mute">{catalog.counts?.channels || 0} channels</Pill>
            </div>
            {!allowPaid && <Notice tone="info">Free channels only. Tick "Allow paid channels" to select channels with a price; the total is shown before you place an order.</Notice>}
            <div style={{ maxHeight: 480, overflowY: 'auto', display: 'grid', gap: 10 }}>
              {channelGroups.map((g) => (
                <details key={g.broadcaster} open={!!channelSearch} className="gx-panel is-mute">
                  <summary style={{ cursor: 'pointer', padding: '6px 2px' }}>
                    <b>{g.broadcaster}</b> <span className="text-small" style={{ color: 'var(--text-muted)' }}>({g.channels.length})</span>
                  </summary>
                  <div className="gx-list" style={{ display: 'grid', gap: 6, paddingTop: 8 }}>
                    {g.channels.map((c) => {
                      const paid = Number(c.price) > 0;
                      const disabled = paid && !allowPaid;
                      const on = pickedChannels.has(c.label);
                      return (
                        <label key={c.label} className={`gx-item${on ? ' is-selected' : ''}`} title={disabled ? 'Tick "Allow paid channels" to select this paid channel' : undefined}
                          style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? .55 : 1, padding: '8px 12px' }}>
                          <input type="checkbox" checked={on} disabled={disabled} onChange={() => toggleChannel(c.label)} style={{ marginTop: 3 }} />
                          <span style={{ flex: 1, minWidth: 0 }}><b>{c.label}</b></span>
                          <Pill tone={paid ? 'warn' : 'ok'}>{fmtRs(c.price ?? 0)}</Pill>
                        </label>
                      );
                    })}
                  </div>
                </details>
              ))}
              {channelGroups.length === 0 && <Empty title="No channel matches" />}
            </div>
          </div>
        )}
      </Panel>

      <Panel icon={Wallet} tone="adv" title="2. Validity and customer types"
        right={summary && (
          <button type="button" className="btn btn-outline-danger btn-sm" disabled={!canOrder || busy || isRunning}
            onClick={() => setShowRestart(true)} title="Start a new round: every active customer becomes eligible again">
            <Repeat size={13} /> Restart (new round)
          </button>)}
        sub={summary?.roundStartedAt
          ? `This round started ${fmtDateTime(summary.roundStartedAt)}${summary.roundStartedBy ? ` by ${summary.roundStartedBy}` : ''} · old data ${summary.roundKeepOld ? 'kept' : 'ignored'}`
          : 'First round for this selection'}>
        <div className="gx-form">
          <div className="gx-grid">
            <Field label="Selection">
              <div className="form-control" style={{ minHeight: 38, height: 'auto', overflowWrap: 'anywhere' }}>{selectionDisplay}</div>
            </Field>
            <Field label="Validity">
              <select className="form-control" value={validity} onChange={(e) => setValidity(e.target.value)} disabled={isRunning}>
                {VALIDITY_OPTIONS.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Customer type" hint="Counts are over the whole active CableTV pool; the run only covers the types ticked here.">
            <div className="gx-actions">
              {DURATION_TYPES.map((t) => {
                const on = durationTypes.includes(t.value);
                return (
                  <button key={t.value} type="button" aria-pressed={on} disabled={isRunning}
                    className={`btn btn-sm ${on ? 'btn-primary' : 'btn-secondary'}`} onClick={() => toggleType(t.value)}>
                    {t.label}{pool?.counts ? ` (${pool.counts[t.value] ?? 0})` : ''}
                  </button>
                );
              })}
            </div>
          </Field>
          {!durationTypes.length && <Notice tone="bad">Tick at least one customer type.</Notice>}
          {itemTotal > 0 && (
            <Notice tone={kind === 'broadcaster' || allowPaid ? 'warn' : 'info'} icon={Wallet}>
              {kind === 'broadcaster'
                ? `${selectedPackages.length} broadcaster package${selectedPackages.length === 1 ? '' : 's'} · ${fmtRs(itemTotal)} per customer for ${VALIDITY_OPTIONS.find((v) => v.value === validity)?.label}. This spends the GTPL wallet.`
                : `${selectedChannels.filter((c) => c.price > 0).length} paid channel${selectedChannels.filter((c) => c.price > 0).length === 1 ? '' : 's'} · ${fmtRs(itemTotal)} per customer for ${VALIDITY_OPTIONS.find((v) => v.value === validity)?.label}.`}
            </Notice>
          )}
        </div>

        {summary && (
          <div style={{ marginTop: 14 }}>
            <Stats>
              <Stat tone="ok" value={summary.placed} label="Placed" />
              <Stat tone="mute" value={summary.alreadyActive} label="Already had it" />
              {!!summary.carriedOver && <Stat tone="mute" value={summary.carriedOver} label="Kept from earlier rounds" />}
              <Stat tone={summary.unconfirmed ? 'warn' : 'mute'} value={summary.unconfirmed} label="Unconfirmed (check on GTPL)" />
              <Stat tone={summary.failed ? 'bad' : 'mute'} value={summary.failed} label="Failed (can retry)" />
              <Stat tone="info" value={summary.remaining} label={`Remaining (${typesLabel(durationTypes)})`} />
            </Stats>
          </div>
        )}
      </Panel>

      <Panel icon={Users} tone="info" title="3. Customers" collapsible open={false} count={`${ticked.length} / ${eligible.length}`}
        sub="Leave everyone ticked to cover all remaining customers, or narrow it down.">
        {poolLoading && !pool ? <Loading /> : (
          <div className="gx-form">
            <div className="gx-actions">
              <input className="form-control" style={{ flex: '1 1 220px' }} placeholder="Search name, account or address" value={pickSearch}
                onChange={(e) => setPickSearch(e.target.value)} aria-label="Search eligible customers" />
              <AreaMultiSelect areas={areas} counts={areaCounts} value={pickAreas} onChange={setPickAreas} />
            </div>
            <div className="gx-actions">
              {pickFiltered && <button type="button" className="btn btn-outline btn-sm" onClick={onlyShown}>Only these {shown.length}</button>}
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setUnticked(new Set())}>Select all</button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setUnticked(new Set(eligible.map((c) => c.accountNumber)))}>Select none</button>
            </div>
            <div className="gx-list" style={{ maxHeight: 380, overflowY: 'auto' }}>
              {shown.map((c) => (
                <label key={c.id} className={`gx-item${!unticked.has(c.accountNumber) ? ' is-selected' : ''}`}
                  style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer', padding: '9px 12px' }}>
                  <input type="checkbox" aria-label={`Include ${c.name}`} checked={!unticked.has(c.accountNumber)} onChange={() => togglePick(c.accountNumber)} style={{ marginTop: 3 }} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <b>{c.name}</b> <span className="font-mono text-small" style={{ color: 'var(--text-muted)' }}>{c.accountNumber}</span>
                    <div className="text-small" style={{ color: 'var(--text-muted)' }}>{c.address || '—'}{c.area ? ` · ${c.area}` : ''}</div>
                  </span>
                  <Pill tone="mute">{DURATION_TYPES.find((t) => t.value === c.type)?.label || c.type}</Pill>
                </label>
              ))}
              {shown.length === 0 && <Empty title="No customers match" />}
            </div>
          </div>
        )}
      </Panel>

      <Panel icon={Search} tone="adv" title="4. Review and run" sub="Preview first, then test on one customer, then place for the selected customers.">
        <Details items={[
          ['Selection', selectionDisplay],
          ['Kind', kind === 'broadcaster' ? 'Broadcaster packages' : 'A la Carte channels'],
          ['Validity', VALIDITY_OPTIONS.find((v) => v.value === validity)?.label],
          ['Customer types', typesLabel(durationTypes)],
          ['Customers', `${ticked.length} of ${eligible.length} eligible${allTicked ? ' (all)' : ''}`],
          ['Cost per customer', itemTotal ? fmtRs(itemTotal) : 'Free'],
          ['Paid channels', kind === 'channel' ? (allowPaid ? 'Allowed' : 'Not allowed') : 'Bouquets are paid'],
        ]} />
        <div className="gx-form" style={{ marginTop: 12 }}>
          <Item tone="info" title="Step 1 — Preview (1 customer)"
            sub="Takes the first ticked customer and stops at GTPL's final confirmation without submitting. Then pick the confirmation button in the results below."
            actions={
              <button type="button" className="btn btn-secondary btn-sm" disabled={idle || !pickedCount || !ticked.length || !durationTypes.length}
                onClick={() => start('preview', 1, undefined, pickAccounts(1))}>
                <Eye size={14} /> Preview (1 customer)
              </button>} />
          <Item tone="adv" title="Step 2 — Place orders"
            sub={<>Confirmation button: {orderButtonId ? <code>{orderButtonId}</code> : <span style={{ color: 'var(--text-muted)' }}>not picked yet</span>}</>}
            actions={<>
              <button type="button" className="btn btn-primary btn-sm" disabled={idle || !orderButtonId || !pickedCount || !ticked.length || !durationTypes.length}
                onClick={() => start('place', 1, undefined, pickAccounts(1))}>
                <Send size={14} /> Test on 1 customer
              </button>
              <button type="button" className="btn btn-danger btn-sm" disabled={idle || !orderButtonId || !pickedCount || !ticked.length || !durationTypes.length}
                onClick={() => start('place', 0, undefined, pickAccounts())}>
                <Send size={14} /> {allTicked ? `Place for all remaining (${summary?.remaining ?? ticked.length})` : `Place for selected (${ticked.length})`}
              </button>
            </>} />
          {!!summary?.failed && (
            <Item tone="warn" title={`Retry — ${summary.failed} failed`}
              sub="Places the order again only for customers whose earlier attempt failed. Unconfirmed accounts are never retried — check those on GTPL."
              actions={
                <button type="button" className="btn btn-warning btn-sm" disabled={idle || !orderButtonId || !pickedCount}
                  onClick={() => start('place', 0, { accountNumbers: [] })}>
                  <RotateCcw size={14} /> Retry failed ({summary.failed})
                </button>} />
          )}
        </div>
      </Panel>

      <Panel icon={ListChecks} tone="mute" title="Results" count={pagination.total}
        right={
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => { loadRows(); loadSummary(); }}>
            <RefreshCw size={13} /> Refresh
          </button>}>
        <div className="gx-actions" style={{ marginBottom: 12 }}>
          <select className="form-control" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} style={{ width: 'auto' }} aria-label="Filter results">
            <option value="">All results</option>
            {Object.entries(STATUS_STYLE).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          {summary?.roundStartedAt && (
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '.85rem' }}>
              <input type="checkbox" checked={includeEarlier} onChange={(e) => { setIncludeEarlier(e.target.checked); setPage(1); }} />
              Include earlier rounds
            </label>
          )}
          {canOrder && failedOnPage.length > 0 && (
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '.85rem' }}>
              <input type="checkbox" checked={allFailedOnPageSelected} onChange={toggleAllFailedOnPage} />
              Select all failed on this page
            </label>
          )}
          {canOrder && selected.size > 0 && (
            <>
              <button type="button" className="btn btn-sm btn-warning" disabled={busy || isRunning || !orderButtonId || !pickedCount}
                title={orderButtonId ? 'Place the ticked failed accounts again' : 'Pick the order button from a preview first'}
                onClick={() => start('place', 0, { accountNumbers: [...selected] })}>
                <RotateCcw size={13} /> Retry selected ({selected.size})
              </button>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setSelected(new Set())}>Clear</button>
            </>
          )}
        </div>

        {rows.length === 0 ? (
          <Empty title={pickedCount ? 'No results yet' : 'Pick something to add first'} />
        ) : (
          <div className="gx-list">
            {rows.map((r) => (
              <Item key={r._id} tone={STATUS_STYLE[r.status]?.tone === 'mute' ? undefined : STATUS_STYLE[r.status]?.tone}
                selected={selected.has(r.accountNumber)}
                left={canOrder && r.status === 'failed'
                  ? <input type="checkbox" aria-label={`Select ${r.accountNumber}`} checked={selected.has(r.accountNumber)} onChange={() => toggleSelected(r.accountNumber)} style={{ marginTop: 3 }} />
                  : null}
                title={r.name || '—'}
                sub={<><span style={{ fontFamily: 'monospace' }}>{r.accountNumber}</span> · {r.kind === 'broadcaster' ? 'Broadcaster' : 'Channels'} · {r.items?.length ? r.items.join(' + ') : r.channelName}</>}
                right={<StatusBadge status={r.status} />}
                meta={<>
                  <span>{r.message || '—'}</span>
                  <span>{fmtDateTime(r.createdAt)}</span>
                  {r.status === 'previewed' && !(r.candidates || []).length && <Pill tone="warn">No submit button found on this page</Pill>}
                </>}
                actions={<>
                  {r.status === 'failed' && canOrder && (
                    <button type="button" className="btn btn-sm btn-outline-secondary" disabled={busy || isRunning || !orderButtonId || !pickedCount}
                      title={orderButtonId ? 'Place this account again' : 'Pick the order button from a preview first'}
                      onClick={() => start('place', 0, { accountNumbers: [r.accountNumber] })}>
                      <RotateCcw size={12} /> Retry
                    </button>
                  )}
                  {r.status === 'previewed' && r.hasScreenshot && (
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setShot(r)}>
                      <Image size={12} /> View page
                    </button>
                  )}
                  {r.status === 'previewed' && (r.candidates || []).map((b) => (
                    <button key={b.id} type="button" className={`btn btn-sm ${orderButtonId === b.id ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => setOrderButtonId(b.id)} title={b.id}>
                      Use “{b.text || b.id}” as confirmation button
                    </button>
                  ))}
                </>} />
            ))}
          </div>
        )}

        <Pager page={page} pages={pagination.pages} total={pagination.total} onPage={setPage} />
      </Panel>

      {shot && <ScreenshotModal row={shot} onClose={() => setShot(null)} />}
      {showRestart && <RestartModal summary={summary} busy={busy} onRestart={restart} onClose={() => setShowRestart(false)} />}
    </Screen>
  );
}
